import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import {
  Stack,
  Box,
  StackProps,
  Container,
  Typography,
  Theme,
  useMediaQuery,
  ClickAwayListener,
  IconButton,
  Link,
} from '@mui/material';

import { links } from '../../paths';

import { colors } from '../../theme/palette';

import { LinkComponent } from '../../atoms/link-component';

import { Dashboard } from '../../atoms/icons/dashboard';
import { Statistics } from '../../atoms/icons/statistics';
import { Settings } from '../../atoms/icons/settings';
import { Logout } from '../../atoms/icons/logout';
import { Close } from '../../atoms/icons/close';
import { Reports } from '../../atoms/icons/reports';

import { useSelector } from '../../store/utils';
import { isSolidsunIframe } from '../../utils/url-layout-modes';

import { usePortal } from './portal';

type Props = {
  desktopIsOpen: boolean;
  desktopClose: () => void;
} & StackProps;

export const NavPrimary = ({
  desktopIsOpen,
  desktopClose,
  ...props
}: Props) => {
  const { pathname } = useLocation();

  const [appMessagesEnabled, premium] = useSelector(
    ({ variables: { APP_MESSAGES_DEV, PREMIUM } }) =>
      [APP_MESSAGES_DEV, PREMIUM] as const
  );

  const md = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { portalActive, handlePortalExit } = usePortal();

  const handleClickAway = (e: MouseEvent | TouchEvent) => {
    if (!(md && desktopIsOpen)) {
      return;
    }

    const target = e?.target as any;
    if (
      target &&
      'closest' in target &&
      typeof target.closest === 'function' &&
      target.closest('.desktop-nav-open') !== null
    ) {
      return;
    }

    desktopClose();
  };

  const items = [
    {
      id: 'home',
      label: <FormattedMessage id="nav.home" defaultMessage="Domů" />,
      icon: <Dashboard />,
      link: links.home,
      hidden: isSolidsunIframe(),
    },
    {
      id: 'statistics',
      label: (
        <FormattedMessage id="nav.statistics" defaultMessage="Statistiky" />
      ),
      icon: <Statistics />,
      link: links.statistics,
    },
    {
      id: 'reports',
      label: <FormattedMessage id="nav.reports" defaultMessage="Reporty" />,
      icon: <Reports />,
      link: links.reports,
      hidden: !premium,
    },
    {
      id: 'app-messages',
      label: <FormattedMessage id="nav.app_messages" defaultMessage="Zprávy" />,
      icon: <Settings />,
      link: links.appMessages,
      hidden: !appMessagesEnabled,
    },
    {
      id: 'settings',
      label: <FormattedMessage id="nav.settings" defaultMessage="Nastavení" />,
      icon: <Settings />,
      link: links.settings,
    },
    {
      id: 'logout',
      label: <FormattedMessage id="nav.logout" defaultMessage="Odhlásit se" />,
      icon: <Logout />,
      hidden: !portalActive,
      onClick: handlePortalExit,
    },
  ];

  const dark = premium;

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack
          {...props}
          component={Container}
          sx={{
            flexDirection: { xs: 'row', md: 'column' },
            justifyContent: { xs: 'space-between', md: 'flex-start' },
            position: 'fixed',
            zIndex: 20,
            left: { xs: 0, md: 'auto' },
            right: 0,
            bottom: 0,
            top: { md: 0 },
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'background.paper'
                : dark
                ? colors.richBlack
                : { xs: 'common.white', md: 'secondary.main' },
            pt: 0.75,
            width: { xs: '100%', md: 320 },
            boxShadow: {
              md: '-11px 5px 44px rgba(62, 104, 121, 0.08)',
            },
            transform: {
              md: desktopIsOpen ? 'translateX(0)' : 'translateX(100%)',
            },
            opacity: {
              md: desktopIsOpen ? 1 : 0,
            },
            transition: (t) => t.transitions.create(['transform', 'opacity']),
          }}
        >
          {/* close button */}
          {md && (
            <Box
              sx={{ mt: 6, mb: 6, display: 'flex', justifyContent: 'flex-end' }}
            >
              <IconButton onClick={desktopClose}>
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}

          {items
            .filter(({ hidden }) => !hidden)
            .map(({ id, label, icon, onClick, link }) => (
              <NavItem
                key={id}
                label={label}
                icon={icon}
                active={link === pathname}
                link={link}
                dark={dark}
                onClick={onClick}
              />
            ))}
        </Stack>
      </ClickAwayListener>

      {/* placeholder */}
      <Box sx={{ height: { xs: 120, md: 0 } }} />
    </>
  );
};

type NavItemProps = {
  label: ReactNode;
  icon: ReactNode;
  active?: boolean;
  link?: string;
  onClick?: () => void;
  dark?: boolean;
};

const NavItem = ({
  label,
  icon,
  active,
  onClick,
  link,
  dark,
}: NavItemProps) => {
  return (
    <Link
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        py: 1,
        cursor: 'pointer',
        mb: { md: 3 },
        pr: { md: 1.5 },
      }}
      onClick={onClick}
      to={link || '#'}
      component={LinkComponent}
    >
      <Box
        sx={{
          opacity: ({ palette }) =>
            palette.mode === 'dark' || dark || active ? 1 : 0.4,
          transition: 'ease-in-out 200ms opacity',
          '& .MuiSvgIcon-root': {
            color: ({ palette }) =>
              palette.mode === 'dark' || dark
                ? active
                  ? 'primary.main'
                  : '#AEAEAE'
                : 'text.disabled',
          },
          '& .MuiSvgIcon-root path, & .MuiSvgIcon-root ellipse': {
            strokeWidth: active ? 1.5 : undefined,
            transition: 'ease-in-out 200ms stroke-width',
          },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row-reverse' },
          alignItems: 'center',
        }}
      >
        {icon}
        <Typography
          variant="body1"
          sx={{
            fontWeight: active ? 600 : 400,
            transition: 'ease-in-out 200ms font-weight',
            color: ({ palette }) =>
              palette.mode === 'dark' || dark
                ? active
                  ? 'primary.main'
                  : '#AEAEAE'
                : 'text.disabled',
            mr: { md: 4 },
          }}
        >
          {label}
        </Typography>
      </Box>
    </Link>
  );
};
